import { useEffect, useState } from 'react';
import { Target, Loader2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useAuth } from '@/contexts/AuthContext';
import { useDealershipSettings } from '@/data/hooks/useDealershipSettings';
import { supabase } from '@/integrations/supabase/client';
import { track } from '@/lib/telemetry';
import { toast } from 'sonner';

export function TeamGoalsCard() {
  const { mode, role, dealershipId, userId } = useAuth();
  const { settings, loading, saving, save } = useDealershipSettings();
  const [sold, setSold] = useState(0);
  const [demo, setDemo] = useState(0);
  const [followup, setFollowup] = useState(0);
  const [csi, setCsi] = useState(80);
  const [busy, setBusy] = useState(false);

  const canEdit = mode === 'real' && (role === 'manager' || role === 'admin');

  // Load monthly_sales_target from dealerships
  useEffect(() => {
    if (mode !== 'real' || !dealershipId) return;
    supabase.from('dealerships').select('monthly_sales_target').eq('id', dealershipId).maybeSingle()
      .then(({ data }) => { if (data) setSold(data.monthly_sales_target ?? 0); });
  }, [mode, dealershipId]);

  useEffect(() => {
    setDemo(settings.monthly_demo_target);
    setFollowup(settings.monthly_followup_target);
    setCsi(settings.csi_green_target);
  }, [settings]);

  if (mode !== 'real' || !(role === 'manager' || role === 'admin')) return null;

  const handleSave = async () => {
    if (!dealershipId) return;
    setBusy(true);
    const [{ error: dErr }, { error: sErr }] = await Promise.all([
      supabase.from('dealerships').update({ monthly_sales_target: sold }).eq('id', dealershipId),
      save({ monthly_demo_target: demo, monthly_followup_target: followup, csi_green_target: csi }).then(r => ({ error: r.error })),
    ]);
    setBusy(false);
    if (dErr || sErr) { toast.error('Could not save goals'); return; }
    toast.success('Team goals saved');
    track('team_goals_updated', { userId, dealershipId, mode }, { sold, demo, followup, csi });
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base flex items-center gap-2">
          <Target className="h-4 w-4 text-primary" /> Team Goals
        </CardTitle>
        <CardDescription>Used by Leaderboard pacing and the Today screen.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <Field id="goal-sold" label="Monthly sold target" value={sold} onChange={setSold} />
          <Field id="goal-demo" label="Monthly demo target" value={demo} onChange={setDemo} />
          <Field id="goal-followup" label="Monthly follow-up target" value={followup} onChange={setFollowup} />
          <Field id="goal-csi" label="CSI green-rate target (%)" value={csi} onChange={setCsi} max={100} />
        </div>
        <div className="flex justify-end">
          <Button onClick={handleSave} disabled={busy || saving || loading || !canEdit}>
            {busy || saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Save goals'}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function Field({ id, label, value, onChange, max }: { id: string; label: string; value: number; onChange: (n: number) => void; max?: number }) {
  return (
    <div className="space-y-1.5">
      <Label htmlFor={id} className="text-xs text-muted-foreground">{label}</Label>
      <Input
        id={id}
        type="number"
        min={0}
        max={max}
        value={value}
        onChange={(e) => onChange(Math.max(0, Math.min(max ?? 9999, parseInt(e.target.value || '0', 10))))}
      />
    </div>
  );
}